import { useContext } from 'react';
import { ReportContext } from '../../../context/ReportContext';
import SimpleDataGrid from '../../Tables/SimpleDataGrid';
import SimpleDataGridItem from '../../Tables/SimpleDataGridItem';
import StandardizedDataTable from '../../PrimeAids/StandardizedDataTable';
import { ReactComponent as AlertTriangleIcon } from '../../../images/alert-triangle-icon.svg';
import ExpandableInfoCard, {
    ExpandableInfoCardDescription,
    ExpandableInfoCardDetail,
} from '../../Cards/ExpandableInfoCard';

const UserCreditAggregate = () => {
    const { reportData } = useContext(ReportContext) || {};

    const enrolledTotal = reportData?.enrolledTotal;
    const creditScoreAverage = reportData?.creditScoreAverage;
    const creditScoreVeryPoor = reportData?.creditScoreVeryPoor;
    const creditScorePoor = reportData?.creditScorePoor;
    const creditScoreFair = reportData?.creditScoreFair;
    const creditScoreGood = reportData?.creditScoreGood;
    const creditScoreExcellent = reportData?.creditScoreExcellent;
    const showResults = reportData?.showResults;

    if (showResults === false) {
        return (
            <div className="max-w-3xl">
                <div className="flex items-start space-x-3 bg-yellow-50 border border-yellow-300 rounded-md p-4 text-sm text-yellow-800">
                    <AlertTriangleIcon className="w-5 h-5 shrink-0" />
                    <div>
                        <p className="font-medium mb-1">Insufficient Data</p>
                        <p>
                            There isn't enough credit score data for the selected filters
                            to display the aggregate report.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Build a breakdown table from the count buckets so the user can filter / export
    const breakdownData = [
        {
            range: 'Very Poor (300 - 499)',
            count: creditScoreVeryPoor,
        },
        {
            range: 'Poor (500 - 600)',
            count: creditScorePoor,
        },
        {
            range: 'Fair (601 - 660)',
            count: creditScoreFair,
        },
        {
            range: 'Good (661 - 780)',
            count: creditScoreGood,
        },
        {
            range: 'Excellent (781 - 850)',
            count: creditScoreExcellent,
        },
    ];

    const columns = [
        {
            field: 'range',
            name: 'Credit Score Range',
            dataType: 'multiselect',
            styles: { minWidth: '18rem' },
        },
        {
            field: 'count',
            name: 'Count',
            dataType: 'numeric',
        },
    ];

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h2 className="font-semibold text-slate-800">Enrollment</h2>
                <SimpleDataGrid>
                    <SimpleDataGridItem label="Total Enrolled:" value={enrolledTotal} />
                </SimpleDataGrid>
            </div>

            <div className="space-y-2">
                <h2 className="font-semibold text-slate-800">Credit Score</h2>
                <SimpleDataGrid>
                    <SimpleDataGridItem
                        label="Average Credit Score:"
                        value={Math.round(creditScoreAverage)}
                    />
                </SimpleDataGrid>
                <StandardizedDataTable data={breakdownData} columns={columns} />
            </div>

            <ExpandableInfoCard title="What is this data?">
                <ExpandableInfoCardDescription>
                    This report provides an overview of credit score enrollment and
                    metrics for users within the selected time period and filters. To
                    protect individual user's privacy, a minimum amount of results must be
                    returned in order to display this data.
                </ExpandableInfoCardDescription>
                <ExpandableInfoCardDetail name="Total Enrolled">
                    The total number of users who have enrolled in the credit score
                    feature.
                </ExpandableInfoCardDetail>
                <ExpandableInfoCardDetail name="Average Credit Score">
                    The mean credit score across all enrolled users, rounded to the
                    nearest whole number.
                </ExpandableInfoCardDetail>
                <ExpandableInfoCardDetail name="Credit Score Range Breakdown">
                    The number of users across five credit score categories: Very Poor
                    (300-499), Poor (500-600), Fair (601-660), Good (661-780), and
                    Excellent (781-850).
                </ExpandableInfoCardDetail>
            </ExpandableInfoCard>
        </div>
    );
};

export default UserCreditAggregate;
